import React, { useEffect, useState } from 'react';
import {
  Form, Input, InputNumber, Select, DatePicker, Button, Row, Col, message, Divider, Modal
} from 'antd';
import { PlusOutlined, UserOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { sheepApi, usersApi } from '../api';
import { Sheep } from '../types';

interface UserOption { id: number; name?: string; username: string; }

interface Props {
  initial?: Partial<Sheep>;
  onSuccess: (sheep: Sheep) => void;
  onCancel: () => void;
}

export default function SheepForm({ initial, onSuccess, onCancel }: Props) {
  const [form] = Form.useForm();
  const [userForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [allSheep, setAllSheep] = useState<Sheep[]>([]);
  const [status, setStatus] = useState<string>(initial?.status ?? 'alive');
  const [gender, setGender] = useState<string>(initial?.gender ?? '');
  const [users, setUsers] = useState<UserOption[]>([]);
  const [addUserModal, setAddUserModal] = useState(false);
  const [savingUser, setSavingUser] = useState(false);

  const loadUsers = () =>
    usersApi.list().then((r) => setUsers(r.data));

  useEffect(() => {
    sheepApi.list().then((r) => setAllSheep(r.data));
    loadUsers();
  }, []);

  useEffect(() => {
    if (initial) {
      form.setFieldsValue({
        ...initial,
        birthDate: initial.birthDate ? dayjs(initial.birthDate) : undefined,
        fatherId: initial.fatherId ?? (initial.father as any)?.id ?? null,
        motherId: initial.motherId ?? (initial.mother as any)?.id ?? null,
      });
      if (initial.status) setStatus(initial.status);
      if (initial.gender) setGender(initial.gender);
      if (initial.conceptionDate) {
        form.setFieldValue('conceptionDate', dayjs(initial.conceptionDate));
      }
    }
  }, [initial, form]);

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      const payload = {
      ...values,
      birthDate: values.birthDate?.format('YYYY-MM-DD'),
      conceptionDate: values.conceptionDate?.format('YYYY-MM-DD') ?? null,
      fatherId: values.fatherId ?? null,
      motherId: values.motherId ?? null,
      conceptionMaleId: values.conceptionMaleId ?? null,
    };
      const r = initial?.id
        ? await sheepApi.update(initial.id, payload)
        : await sheepApi.create(payload);
      onSuccess(r.data);
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Ошибка сохранения');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (values: any) => {
    setSavingUser(true);
    try {
      const r = await usersApi.create(values);
      const created = r.data;
      if (created.generatedPassword) {
        message.success(
          `Пользователь создан. Логин: ${created.username}, Пароль: ${created.generatedPassword}`,
          8,
        );
      } else {
        message.success('Пользователь создан');
      }
      await loadUsers();
      form.setFieldValue('ownerId', created.id);
      setAddUserModal(false);
      userForm.resetFields();
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Ошибка');
    } finally {
      setSavingUser(false);
    }
  };

  const males = allSheep.filter((s) => s.gender === 'male' && s.id !== initial?.id);
  const females = allSheep.filter((s) => s.gender === 'female' && s.id !== initial?.id);

  const userOptions = users.map((u) => ({
    value: u.id,
    label: u.name ? `${u.name} (${u.username})` : u.username,
  }));

  return (
    <>
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="name" label="Кличка" rules={[{ required: true }]}>
              <Input placeholder="Арашан" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="tagNumber" label="Номер тега" rules={[{ required: true }]}>
              <Input placeholder="AR-001" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="birthDate" label="Дата рождения" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="gender" label="Пол" rules={[{ required: true }]}>
              <Select onChange={(v) => setGender(v)}>
                <Select.Option value="male">Самец (баран)</Select.Option>
                <Select.Option value="female">Самка (овца)</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="breed" label="Порода">
              <Input placeholder="Алтайский меринос" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="status" label="Статус">
              <Select defaultValue="alive" onChange={(v) => setStatus(v)}>
                <Select.Option value="alive">Живой</Select.Option>
                <Select.Option value="for_sale">В продаже</Select.Option>
                <Select.Option value="sold">Продан</Select.Option>
                <Select.Option value="deceased">Погиб</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="weight" label="Вес (кг)">
              <InputNumber style={{ width: '100%' }} min={0} step={0.1} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="bloodline" label="Линия / кровь">
              <Input placeholder="Линия Арашан, Текел..." />
            </Form.Item>
          </Col>
        </Row>

        {(status === 'for_sale' || status === 'sold') && (
          <Form.Item
            name="salePrice"
            label="Цена продажи (сом)"
            rules={[{ required: true, message: 'Укажите цену продажи' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              step={100}
              placeholder="например 50000"
              formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
              parser={(v) => v?.replace(/\s/g, '') as any}
            />
          </Form.Item>
        )}

        <Form.Item name="ownerId" label="Владелец">
          <Select
            allowClear
            showSearch
            placeholder="Выберите владельца"
            filterOption={(input, opt) =>
              (opt?.label as string)?.toLowerCase().includes(input.toLowerCase())
            }
            options={userOptions}
            dropdownRender={(menu) => (
              <>
                {menu}
                <Divider style={{ margin: '4px 0' }} />
                <Button
                  type="link"
                  icon={<PlusOutlined />}
                  style={{ width: '100%', textAlign: 'left', paddingLeft: 12 }}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setAddUserModal(true)}
                >
                  Добавить пользователя
                </Button>
              </>
            )}
          />
        </Form.Item>

        <Divider style={{ fontSize: 13, color: '#888', margin: '4px 0 8px' }}>Промеры (см)</Divider>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="earLength" label="Длина ушей">
              <InputNumber style={{ width: '100%' }} min={0} step={0.1} placeholder="см" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="withersHeight" label="Рост в холке">
              <InputNumber style={{ width: '100%' }} min={0} step={0.1} placeholder="см" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="boneStructure" label="Костяк">
              <InputNumber style={{ width: '100%' }} min={0} step={0.1} placeholder="см" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="chestGirth" label="Обхват груди">
              <InputNumber style={{ width: '100%' }} min={0} step={0.1} placeholder="см" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="fattailGirth" label="Обхват курдюка">
              <InputNumber style={{ width: '100%' }} min={0} step={0.1} placeholder="см" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="bodyLength" label="Длина">
              <InputNumber style={{ width: '100%' }} min={0} step={0.1} placeholder="см" />
            </Form.Item>
          </Col>
        </Row>

        <Divider style={{ fontSize: 13, color: '#888', margin: '4px 0 8px' }}>Родословная</Divider>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="fatherId" label="Отец">
              <Select allowClear showSearch
                filterOption={(input, opt) =>
                  (opt?.label as string)?.toLowerCase().includes(input.toLowerCase())
                }
                options={males.map((s) => ({
                  value: s.id, label: `${s.name} (${s.tagNumber})`,
                }))} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="motherId" label="Мать">
              <Select allowClear showSearch
                filterOption={(input, opt) =>
                  (opt?.label as string)?.toLowerCase().includes(input.toLowerCase())
                }
                options={females.map((s) => ({
                  value: s.id, label: `${s.name} (${s.tagNumber})`,
                }))} />
            </Form.Item>
          </Col>
        </Row>

        {gender === 'female' && (
          <>
            <Divider style={{ fontSize: 13, color: '#888', margin: '4px 0 8px' }}>Беременность</Divider>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="lambingNumber" label="Какой окот по счёту">
                  <InputNumber style={{ width: '100%' }} min={1} max={20} placeholder="1, 2, 3…" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="conceptionDate" label="Дата зачатия">
                  <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="conceptionMaleId" label="От кого (баран)">
                  <Select allowClear showSearch placeholder="Выберите барана"
                    filterOption={(input, opt) =>
                      (opt?.label as string)?.toLowerCase().includes(input.toLowerCase())
                    }
                    options={males.map((s) => ({
                      value: s.id, label: `${s.name} (${s.tagNumber})`,
                    }))} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.conceptionDate !== cur.conceptionDate}>
              {({ getFieldValue }) => {
                const cd = getFieldValue('conceptionDate');
                if (!cd) return null;
                const from = dayjs(cd).add(147, 'day').format('DD.MM.YYYY');
                const to = dayjs(cd).add(150, 'day').format('DD.MM.YYYY');
                return (
                  <div style={{
                    background: '#f6ffed', border: '1px solid #b7eb8f',
                    borderRadius: 8, padding: '10px 14px', marginBottom: 12,
                  }}>
                    🐣 <b>Примерная дата родов:</b> {from} — {to}
                  </div>
                );
              }}
            </Form.Item>
          </>
        )}

        <Form.Item name="notes" label="Заметки">
          <Input.TextArea rows={3} />
        </Form.Item>
        <Row justify="end" gutter={8}>
          <Col><Button onClick={onCancel}>Отмена</Button></Col>
          <Col>
            <Button type="primary" htmlType="submit" loading={loading}>
              {initial?.id ? 'Сохранить' : 'Добавить'}
            </Button>
          </Col>
        </Row>
      </Form>

      <Modal
        title="Новый пользователь"
        open={addUserModal}
        onCancel={() => { setAddUserModal(false); userForm.resetFields(); }}
        footer={null}
        destroyOnClose
      >
        <Form form={userForm} layout="vertical" onFinish={handleAddUser} style={{ marginTop: 16 }}>
          <Form.Item name="name" label="Имя" rules={[{ required: true, message: 'Введите имя' }]}>
            <Input placeholder="Айбек Карагулов" />
          </Form.Item>
          <Form.Item name="username" label="Логин (необязательно — сгенерируется автоматически)">
            <Input prefix={<UserOutlined />} placeholder="aibek" />
          </Form.Item>
          <Form.Item name="password" label="Пароль (необязательно — сгенерируется автоматически)"
            rules={[{ min: 6, message: 'Минимум 6 символов' }]}>
            <Input.Password placeholder="минимум 6 символов" />
          </Form.Item>
          <Form.Item name="role" label="Роль" initialValue="user">
            <Select>
              <Select.Option value="user">Пользователь</Select.Option>
              <Select.Option value="admin">Администратор</Select.Option>
            </Select>
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => { setAddUserModal(false); userForm.resetFields(); }}>Отмена</Button>
            <Button type="primary" htmlType="submit" loading={savingUser}>Создать</Button>
          </div>
        </Form>
      </Modal>
    </>
  );
}
